import { useEffect, useState } from "react";
import { Folder, Plus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function CollectionPicker({ scan }) {
  const [open, setOpen] = useState(false);
  const [cols, setCols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set(scan.collection_ids || []));
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/collections");
      setCols(r.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && cols.length === 0) load();
  }, [open]); // eslint-disable-line

  const toggle = async (c) => {
    const next = new Set(selected);
    const add = !next.has(c.id);
    if (add) next.add(c.id); else next.delete(c.id);
    setSelected(next);
    try {
      await api.post(`/collections/assign/${scan.id}`, { collection_id: c.id, add });
      toast.success(add ? `Added to ${c.name}` : `Removed from ${c.name}`);
    } catch (e) {
      // revert on failure
      if (add) next.delete(c.id); else next.add(c.id);
      setSelected(new Set(next));
      toast.error("Update failed");
    }
  };

  const createFolder = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      const r = await api.post("/collections", { name });
      setCols((c) => [...c, r.data]);
      setNewName(""); setCreating(false);
      // immediately assign
      await toggle(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div className="mt-3 rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="collection-picker">
      <button
        data-testid="collection-picker-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2.5 flex items-center gap-2 text-left"
      >
        <div className="w-8 h-8 rounded-xl bg-emerald-400/15 border border-emerald-400/40 text-emerald-300 flex items-center justify-center">
          <Folder size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading font-semibold leading-tight">Save to folder</p>
          <p className="text-[11px] text-slate-400 leading-tight">
            {selected.size === 0 ? "Not in any folder" : `In ${selected.size} folder${selected.size === 1 ? "" : "s"}`}
          </p>
        </div>
        <span className="text-[10px] font-mono-data uppercase text-slate-500">{open ? "Hide" : "Open"}</span>
      </button>

      {open && (
        <div className="px-3 pb-3">
          {loading && <div className="py-3 flex justify-center"><Loader2 className="animate-spin text-slate-500" size={18} /></div>}
          <div className="flex flex-wrap gap-1.5">
            {cols.map((c) => {
              const on = selected.has(c.id);
              return (
                <button
                  key={c.id}
                  data-testid={`collection-chip-${c.id}`}
                  onClick={() => toggle(c)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs active:scale-95 transition-colors ${on ? "text-white" : "bg-white/5 border-white/10 text-slate-300"}`}
                  style={on ? { background: `${c.color}22`, borderColor: `${c.color}aa`, color: c.color } : {}}
                >
                  <span>{c.emoji || "📁"}</span>
                  <span className="font-medium">{c.name}</span>
                  {on && <Check size={12} />}
                </button>
              );
            })}
            {!creating && (
              <button
                data-testid="collection-create-toggle"
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-dashed border-cyan-400/40 text-xs text-cyan-300 active:scale-95"
              >
                <Plus size={12} /> New folder
              </button>
            )}
          </div>

          {creating && (
            <form onSubmit={createFolder} className="mt-2 flex items-center gap-2">
              <input
                data-testid="collection-new-name"
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Folder name"
                className="flex-1 h-9 px-3 rounded-xl bg-black/30 border border-white/10 outline-none focus:border-cyan-400/60 text-sm"
              />
              <button data-testid="collection-create-submit" type="submit"
                className="h-9 px-3 rounded-xl bg-cyan-400 text-slate-950 font-semibold text-xs active:scale-95">
                Create
              </button>
              <button type="button" onClick={() => { setCreating(false); setNewName(""); }} className="text-slate-400 text-xs">Cancel</button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
